import Cookies from 'js-cookie'
import { useState, createContext, useContext } from 'react'
import getConfig from 'next/config'
import { useLoggedInUser } from '../LoggedInUserContext'
import Router from 'next/router'
import { track } from '~/utils/analytics'
import { LOCALE_BAR_DISMISSED } from '~/lib/tracking-events'
import { getBrowserLocale } from '~/utils/localization'

const { LOCALE_NOTIFICATION_COOKIE_NAME } = getConfig().publicRuntimeConfig

export const LocaleBarContext = createContext()

function LocaleBarProvider({ children, language }) {
  const { loggedInUser } = useLoggedInUser()
  const browserLanguage = getBrowserLocale()
  const userLanguage = loggedInUser?.locale?.split('_')[0] || null
  const browserLanguageContainsSystemLanguage = browserLanguage.indexOf(language) !== -1
  const hasCookie = Cookies.get(LOCALE_NOTIFICATION_COOKIE_NAME) === '1'
  const initialShow =
    browserLanguage !== language &&
    userLanguage !== language &&
    !browserLanguageContainsSystemLanguage &&
    !hasCookie

  const [visible, setVisible] = useState(initialShow)
  const show = () => setVisible(true)
  const hide = () => {
    track(LOCALE_BAR_DISMISSED)
    Cookies.set(LOCALE_NOTIFICATION_COOKIE_NAME, 1, { secure: true, sameSite: 'lax' })
    setVisible(false)
  }

  Router.events.on('routeChangeStart', () => {
    // Hide locale-bar on subsequent page-views
    if (!hasCookie) {
      hide()
    }
  })

  return (
    <LocaleBarContext.Provider value={{ visible, show, hide, hasCookie }}>
      {children}
    </LocaleBarContext.Provider>
  )
}

export function useLocaleBar() {
  const context = useContext(LocaleBarContext)

  if (!context) {
    throw new Error('useLocaleBar() must be used within a LocaleBarProvider')
  }

  const { visible, show, hide, hasCookie } = context

  return {
    visible,
    show,
    hide,
    hasCookie,
  }
}

export default LocaleBarProvider
